import Axios from "axios";
import {
    Elements,
    ExpressCheckoutElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import {
    ComponentProps,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { NotificationManager } from "react-notifications";
import { stripePromise } from "context/stripe-provider";
import useAnalytics from "hooks/analytics";
import useFeatureManager from "hooks/featureManager";
import useLanguage from "hooks/language";
import { FeatureFlag } from "utils/feature-management/amplitude";
import { UserActionEvent } from "utils/analytics/events";
import ENDPOINTS from "utils/endpoints";
import { getAppointmentHold } from "utils/session-storage";
import { ScreeningPaymentType } from "../payment-types";

import styles from "./express-wallet-checkout.module.css";

export const EXPRESS_WALLET_CHECKOUT_FLAG = "express-wallet-checkout";
export const EXPRESS_AMOUNT_CENTS = 5500;
export const NO_SHOW_FEE_CENTS = 3500;
/** $35 + TPS 5% + TVQ 9.975% — matches the admin-fee invoice total. */
export const NO_SHOW_FEE_WITH_TAX_CENTS = 4024;
/** Unblock card-only checkout if Stripe.js or Express Checkout never becomes ready. */
const EXPRESS_CHECKOUT_INIT_TIMEOUT_MS = 15000;

export const WalletCheckoutFlow = {
    ExpressPayPublic: "express-pay-public",
    ExpressPayPrivate: "express-pay-private",
    ExpressPayHpv: "express-pay-hpv",
    LateReschedule: "late-reschedule",
    CardOnFilePublic: "card-on-file-public",
} as const;

export type WalletCheckoutFlowValue = typeof WalletCheckoutFlow[keyof typeof WalletCheckoutFlow];

type ExpressPaymentType = "apple_pay" | "google_pay";

type ExpressWalletCheckoutProps = {
    flow: WalletCheckoutFlowValue;
    onPaid: (method: string, paymentMethodId?: string) => Promise<void> | void;
    onProcessingChange?: (isProcessing: boolean) => void;
    amountCents?: number;
    /** Save a payment method without charging (booking-flow phone-call consult). */
    checkoutMode?: "payment" | "setup";
    deferredNoShow?: boolean;
    saveCard?: boolean;
    walletPaymentDescription?: string;
    walletNoShowLabel?: string;
    intro?: ReactNode;
    onAvailabilityChange?: (isAvailable: boolean) => void;
    /** Fired when wallet buttons have mounted (iframe present) or polling timed out. */
    onWalletButtonsPainted?: () => void;
    /** When false, painted callback waits until this becomes true (e.g. card info still loading). */
    walletPaintSignalEnabled?: boolean;
    hideOrDivider?: boolean;
    disabled?: boolean;
};

const WALLET_IFRAME_POLL_MAX_FRAMES = 120;
const WALLET_IFRAME_STABLE_FRAMES = 3;
const WALLET_IFRAME_MIN_SIZE_PX = 24;

function walletButtonsLookReady(root: HTMLElement | null): boolean {
    const iframe = root?.querySelector("iframe");
    if (!iframe) {
        return false;
    }
    const { width, height } = iframe.getBoundingClientRect();
    return (
        width >= WALLET_IFRAME_MIN_SIZE_PX &&
        height >= WALLET_IFRAME_MIN_SIZE_PX
    );
}

function scheduleWalletButtonsPainted(
    root: HTMLElement | null,
    onPainted: () => void
): () => void {
    let frame = 0;
    let stableReadyFrames = 0;
    let rafId = 0;

    const tick = () => {
        frame += 1;
        if (walletButtonsLookReady(root)) {
            stableReadyFrames += 1;
            if (stableReadyFrames >= WALLET_IFRAME_STABLE_FRAMES) {
                onPainted();
                return;
            }
        } else {
            stableReadyFrames = 0;
        }
        if (frame >= WALLET_IFRAME_POLL_MAX_FRAMES) {
            onPainted();
            return;
        }
        rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);

    return () => {
        if (rafId) {
            window.cancelAnimationFrame(rafId);
        }
    };
}

function tomorrow(): Date {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    date.setHours(0, 0, 0, 0);
    return date;
}

function deferredNoShowDate(): Date {
    const hold = getAppointmentHold();
    if (!hold?.datetime) {
        return tomorrow();
    }

    const appointmentDate = new Date(hold.datetime);
    if (
        Number.isNaN(appointmentDate.getTime()) ||
        appointmentDate <= new Date()
    ) {
        return tomorrow();
    }

    return appointmentDate;
}

function paymentTypeFromExpress(
    expressPaymentType: string | undefined
): string | null {
    if (expressPaymentType === "apple_pay") {
        return ScreeningPaymentType.ApplePay;
    }
    if (expressPaymentType === "google_pay") {
        return ScreeningPaymentType.GooglePay;
    }
    return null;
}

function WalletButtons({
    flow,
    onPaid,
    onProcessingChange,
    deferredNoShow,
    saveCard,
    walletPaymentDescription,
    walletNoShowLabel,
    intro,
    onAvailabilityChange,
    onWalletButtonsPainted,
    walletPaintSignalEnabled = true,
    hideOrDivider = false,
    disabled = false,
}: Required<Pick<ExpressWalletCheckoutProps, "deferredNoShow" | "saveCard">> &
    ExpressWalletCheckoutProps): JSX.Element | null {
    const stripe = useStripe();
    const elements = useElements();
    const [analytics] = useAnalytics();
    const { translations } = useLanguage();
    const [isWalletAvailable, setIsWalletAvailable] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const confirmStartedRef = useRef(false);
    const sheetOpenRef = useRef(false);
    const lastWalletMethodRef = useRef<string | null>(null);
    const availabilityReportedRef = useRef(false);
    const initTimeoutRef = useRef<number | null>(null);
    const walletRootRef = useRef<HTMLDivElement>(null);
    const walletPaintPollCleanupRef = useRef<(() => void) | null>(null);
    const walletAvailableRef = useRef(false);
    const walletPaintedRef = useRef(false);
    const t = translations.upfrontPaymentSection.paymentForm;

    const startWalletPaintPoll = useCallback(() => {
        if (
            !onWalletButtonsPainted ||
            walletPaintedRef.current ||
            !walletAvailableRef.current ||
            !walletPaintSignalEnabled
        ) {
            return;
        }
        walletPaintPollCleanupRef.current?.();
        walletPaintPollCleanupRef.current = scheduleWalletButtonsPainted(
            walletRootRef.current,
            () => {
                if (walletPaintedRef.current) {
                    return;
                }
                walletPaintedRef.current = true;
                onWalletButtonsPainted();
            }
        );
    }, [onWalletButtonsPainted, walletPaintSignalEnabled]);

    useEffect(() => {
        if (isWalletAvailable && walletPaintSignalEnabled) {
            startWalletPaintPoll();
        }
    }, [isWalletAvailable, walletPaintSignalEnabled, startWalletPaintPoll]);

    useEffect(
        () => () => {
            walletPaintPollCleanupRef.current?.();
        },
        []
    );

    const reportAvailability = useCallback(
        (isAvailable: boolean) => {
            if (availabilityReportedRef.current) {
                return;
            }
            availabilityReportedRef.current = true;
            if (initTimeoutRef.current !== null) {
                window.clearTimeout(initTimeoutRef.current);
                initTimeoutRef.current = null;
            }
            onAvailabilityChange?.(isAvailable);
        },
        [onAvailabilityChange]
    );

    useEffect(() => {
        if (!onAvailabilityChange || availabilityReportedRef.current) {
            return undefined;
        }

        initTimeoutRef.current = window.setTimeout(() => {
            reportAvailability(false);
        }, EXPRESS_CHECKOUT_INIT_TIMEOUT_MS);

        return () => {
            if (initTimeoutRef.current !== null) {
                window.clearTimeout(initTimeoutRef.current);
                initTimeoutRef.current = null;
            }
        };
    }, [onAvailabilityChange, reportAvailability]);

    const setProcessing = (isProcessing: boolean) => {
        setIsConfirming(isProcessing);
        onProcessingChange?.(isProcessing);
    };

    const expressCheckoutOptions = useMemo(() => {
        const options: ComponentProps<
            typeof ExpressCheckoutElement
        >["options"] = {
            buttonHeight: 44,
            paymentMethods: {
                applePay: "always",
                googlePay: "always",
                link: "never",
                paypal: "never",
            },
        };

        if (deferredNoShow) {
            options.applePay = {
                deferredPaymentRequest: {
                    paymentDescription:
                        walletPaymentDescription ?? t.walletPaymentDescription,
                    managementURL: `${window.location.origin}/profile`,
                    deferredBilling: {
                        amount: NO_SHOW_FEE_WITH_TAX_CENTS,
                        label: walletNoShowLabel ?? t.walletNoShowLabel,
                        deferredPaymentDate: deferredNoShowDate(),
                    },
                },
            };
        }

        return options;
    }, [
        deferredNoShow,
        t.walletNoShowLabel,
        t.walletPaymentDescription,
        walletNoShowLabel,
        walletPaymentDescription,
    ]);

    if (!stripe || !elements) {
        return null;
    }

    return (
        <div
            ref={walletRootRef}
            className={`${styles.walletCheckout} ${
                disabled ? styles.walletCheckoutDisabled : ""
            }`}
            hidden={!isWalletAvailable && !isConfirming}
            aria-disabled={disabled || undefined}
        >
            {intro && <p className={styles.walletCheckout__intro}>{intro}</p>}
            <ExpressCheckoutElement
                options={expressCheckoutOptions}
                onReady={({ availablePaymentMethods }) => {
                    const isAvailable =
                        !!availablePaymentMethods?.applePay ||
                        !!availablePaymentMethods?.googlePay;
                    walletAvailableRef.current = isAvailable;
                    setIsWalletAvailable(isAvailable);
                    reportAvailability(isAvailable);
                }}
                onClick={(event) => {
                    if (disabled) {
                        return;
                    }
                    confirmStartedRef.current = false;
                    sheetOpenRef.current = true;
                    setProcessing(true);
                    const method =
                        paymentTypeFromExpress(event.expressPaymentType) ??
                        event.expressPaymentType;
                    lastWalletMethodRef.current = method;
                    analytics
                        ?.cdp()
                        ?.trackEvent(UserActionEvent.WalletCheckoutClicked, {
                            method,
                            flow,
                        });
                    event.resolve();
                }}
                onCancel={() => {
                    // Stripe can fire cancel after confirm when the wallet
                    // sheet dismisses. Keep the lock until onConfirm finishes.
                    if (confirmStartedRef.current) {
                        return;
                    }
                    if (sheetOpenRef.current) {
                        sheetOpenRef.current = false;
                        analytics
                            ?.cdp()
                            ?.trackEvent(
                                UserActionEvent.WalletCheckoutCancelled,
                                {
                                    method: lastWalletMethodRef.current,
                                    flow,
                                }
                            );
                    }
                    setProcessing(false);
                }}
                onConfirm={async (event) => {
                    if (disabled) {
                        return;
                    }
                    const failWallet = (message: string) => {
                        NotificationManager.error(message);
                        event.paymentFailed({ reason: "fail" });
                    };

                    confirmStartedRef.current = true;
                    sheetOpenRef.current = false;
                    setProcessing(true);
                    try {
                        const method = paymentTypeFromExpress(
                            event.expressPaymentType as ExpressPaymentType
                        );
                        if (!method) {
                            failWallet(t.walletUnavailable);
                            return;
                        }

                        const { error: submitError } = await elements.submit();
                        if (submitError) {
                            failWallet(
                                submitError.message ?? t.walletUnavailable
                            );
                            return;
                        }

                        const {
                            error,
                            paymentMethod,
                        } = await stripe.createPaymentMethod({
                            elements,
                        });
                        if (error || !paymentMethod) {
                            failWallet(error?.message ?? t.walletUnavailable);
                            return;
                        }

                        if (saveCard) {
                            await Axios.put(ENDPOINTS.USER_PAYMENT_INFO, {
                                paymentMethodId: paymentMethod.id,
                            });
                        }
                        await onPaid(method, paymentMethod.id);
                        analytics
                            ?.cdp()
                            ?.trackEvent(
                                UserActionEvent.WalletCheckoutConfirmed,
                                {
                                    method,
                                    flow,
                                }
                            );
                    } catch (error) {
                        // Axios already toasted via the global interceptor.
                        if (
                            typeof error === "object" &&
                            error !== null &&
                            (error as { isAxiosError?: boolean }).isAxiosError
                        ) {
                            event.paymentFailed({ reason: "fail" });
                            return;
                        }
                        const message =
                            error instanceof Error
                                ? error.message
                                : t.walletUnavailable;
                        failWallet(message);
                    } finally {
                        confirmStartedRef.current = false;
                        setProcessing(false);
                    }
                }}
            />
            {!hideOrDivider && (
                <p className={styles.walletCheckout__or}>{t.walletOrCard}</p>
            )}
        </div>
    );
}

export default function ExpressWalletCheckout({
    flow,
    onPaid,
    onProcessingChange,
    amountCents = EXPRESS_AMOUNT_CENTS,
    checkoutMode = "payment",
    deferredNoShow = true,
    saveCard = true,
    walletPaymentDescription,
    walletNoShowLabel,
    intro,
    onAvailabilityChange,
    onWalletButtonsPainted,
    walletPaintSignalEnabled = true,
    hideOrDivider = false,
    disabled = false,
}: ExpressWalletCheckoutProps): JSX.Element | null {
    const [featureManager] = useFeatureManager();
    const { language } = useLanguage();

    const isEnabled = featureManager?.isFeatureFlagEnabled(
        EXPRESS_WALLET_CHECKOUT_FLAG,
        FeatureFlag.On
    );
    if (!isEnabled) {
        return null;
    }

    const elementsOptions =
        checkoutMode === "setup"
            ? {
                  mode: "setup" as const,
                  currency: "cad",
                  paymentMethodCreation: "manual" as const,
                  setupFutureUsage: "off_session" as const,
                  locale: language,
              }
            : {
                  mode: "payment" as const,
                  amount: amountCents,
                  currency: "cad",
                  ...(saveCard
                      ? { setupFutureUsage: "off_session" as const }
                      : {}),
                  paymentMethodCreation: "manual" as const,
                  locale: language,
              };

    // Sibling of CardElementsProvider — do not nest this under another Elements.
    return (
        <Elements stripe={stripePromise} options={elementsOptions}>
            <WalletButtons
                flow={flow}
                onPaid={onPaid}
                onProcessingChange={onProcessingChange}
                deferredNoShow={deferredNoShow}
                saveCard={saveCard}
                walletPaymentDescription={walletPaymentDescription}
                walletNoShowLabel={walletNoShowLabel}
                intro={intro}
                onAvailabilityChange={onAvailabilityChange}
                onWalletButtonsPainted={onWalletButtonsPainted}
                walletPaintSignalEnabled={walletPaintSignalEnabled}
                hideOrDivider={hideOrDivider}
                disabled={disabled}
            />
        </Elements>
    );
}
